// "For whom?" picker on the household add-record forms (0232, Grant
// 2026-08-03: a service record cannot exist without a person).
//
// Renders the household's members as toggle chips; at least one must
// stay selected (Save is blocked otherwise — the parent checks
// selected.length). Defaults are pre-selected so a lone-member
// household costs zero clicks (Minimum Viable Typing): the parent
// initializes selection via defaultPersonSelection().

import type { HouseholdContact } from "../../api";

export function memberDisplayName(m: HouseholdContact): string {
  return (
    [m.preferred_name || m.legal_first_name, m.legal_last_name]
      .filter(Boolean)
      .join(" ")
      .trim() || "Unnamed contact"
  );
}

/** Default selection for a create form: lone member auto-selected;
 *  multi-member pre-selects the household primary (the human sees it
 *  checked and can change it — in-flow confirmation, so the server
 *  stores it as a human choice). */
export function defaultPersonSelection(
  members: HouseholdContact[],
  primaryContactId: string | null | undefined,
): string[] {
  if (members.length === 1) return [members[0]!.id];
  if (primaryContactId && members.some((m) => m.id === primaryContactId)) {
    return [primaryContactId];
  }
  return [];
}

export function PersonSelect({
  label,
  members,
  selected,
  onChange,
  disabled,
  onAddPerson,
}: {
  /** Per-company noun — "Insured", "Borrower", "Account holder", … */
  label: string;
  members: HouseholdContact[];
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Jumps to the household's Members tab. When absent, the empty state
   *  falls back to plain text (unwired callers). */
  onAddPerson?: (() => void) | undefined;
}) {
  function toggle(id: string) {
    onChange(
      selected.includes(id)
        ? selected.filter((x) => x !== id)
        : [...selected, id],
    );
  }

  return (
    <label style={{ display: "block", fontSize: 11, color: "var(--fg-muted)" }}>
      {label}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          marginTop: 4,
        }}
      >
        {members.map((m) => {
          const on = selected.includes(m.id);
          return (
            <button
              key={m.id}
              type="button"
              disabled={disabled}
              onClick={() => toggle(m.id)}
              aria-pressed={on}
              style={{
                fontSize: 12,
                padding: "3px 10px",
                borderRadius: 999,
                cursor: disabled ? "default" : "pointer",
                border: on
                  ? "1px solid var(--blue, #4e98c6)"
                  : "1px solid var(--mid-gray, #e0e6ec)",
                background: on ? "var(--blue, #4e98c6)" : "transparent",
                color: on ? "#fff" : "var(--fg, inherit)",
              }}
            >
              {memberDisplayName(m)}
            </button>
          );
        })}
        {members.length === 0 && onAddPerson && (
          <button
            type="button"
            onClick={onAddPerson}
            disabled={disabled}
            style={{
              fontSize: 12,
              color: "var(--blue, #4e98c6)",
              background: "none",
              border: "none",
              padding: 0,
              cursor: disabled ? "default" : "pointer",
              textDecoration: "underline",
            }}
          >
            No people in this household yet — add the person first.
          </button>
        )}
        {members.length === 0 && !onAddPerson && (
          <span style={{ fontSize: 12 }}>
            No people in this household yet — add the person first.
          </span>
        )}
      </div>
      {selected.length === 0 && members.length > 0 && (
        <div style={{ fontSize: 11, marginTop: 3, color: "var(--fg-muted)" }}>
          Pick at least one person — every record belongs to someone.
        </div>
      )}
    </label>
  );
}
